import { sectionManager } from '@fc/sections';
import { parsePositiveInt, preserveMarketContext, preloadCardImage, sameProductPath } from '@fc/quick-add-utils';

const QUICK_ADD_PREWARM_LIMIT = 12;
const QUICK_ADD_FALLBACK_AVAILABILITY_LIMIT = 24;

export class QuickAddVariantResolver {
  #host;
  #variantMap = undefined;
  #prewarmedProductCardUrls = new Set();
  #fallbackUnavailableKeys = new Set();

  constructor(host) {
    this.#host = host;
  }

  selectedCardOptionState(button = null) {
    const card = this.#host.closest('product-tile, product-card');
    const selectedValuesByPosition = new Map();
    const selectedNamesByPosition = new Map();
    let productUrl = this.#host.dataset.productUrl || '';
    if (!card) return { selectedValuesByPosition, selectedNamesByPosition, productUrl };

    card.querySelectorAll('input[type="radio"]:checked[data-option-value-id]').forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      if (radio.closest('quick-add') === this.#host) return;

      const position = this.optionPosition(radio);
      if (!position) return;

      const optionValueId = radio.dataset.optionValueId;
      if (!optionValueId) return;

      selectedValuesByPosition.set(position, optionValueId);
      selectedNamesByPosition.set(position, radio.dataset.optionValueName || radio.value || '');
      productUrl = radio.dataset.connectedProductUrl || radio.dataset.productUrl || productUrl;
    });

    if (button instanceof HTMLElement) {
      const buttonPosition = this.optionPosition(button);
      const buttonOptionValueId = button.dataset.optionValueId;

      if (buttonPosition && buttonOptionValueId) {
        selectedValuesByPosition.set(buttonPosition, buttonOptionValueId);
      }
      if (buttonPosition) {
        selectedNamesByPosition.set(
          buttonPosition,
          button.dataset.optionValueName || button.dataset.optionValue || button.dataset.sizeValue || button.textContent?.trim() || '',
        );
      }

      productUrl = button.dataset.connectedProductUrl || button.dataset.productUrl || productUrl || '';
    }

    return { selectedValuesByPosition, selectedNamesByPosition, productUrl };
  }

  optionPosition(element) {
    const raw =
      element?.dataset?.fitOptionPosition ||
      element?.dataset?.inputId?.split('-')?.[0] ||
      element?.dataset?.pickerOptionPosition ||
      this.#host.dataset.pickerOptionPosition ||
      this.#host.dataset.sizeOptionPosition ||
      '';
    const position = Number.parseInt(raw, 10);
    return Number.isFinite(position) && position > 0 ? position : null;
  }

  optionCount() {
    const declared = parsePositiveInt(this.#host.dataset.optionCount);
    if (declared > 0) return Math.min(declared, 3);

    return 0;
  }

  selectedOptionValueIds(button = null) {
    return this.selectedOptionValueIdsFromState(this.selectedCardOptionState(button));
  }

  selectedOptionValueIdsFromState(state) {
    return Array.from(state.selectedValuesByPosition.entries())
      .sort(([positionA], [positionB]) => positionA - positionB)
      .map(([, optionValueId]) => optionValueId)
      .filter(Boolean);
  }

  quickAddVariantMap() {
    if (this.#variantMap !== undefined) return this.#variantMap;

    const script = this.#host.querySelector('[data-quick-add-variant-map]');
    if (!script?.textContent) {
      this.#variantMap = null;
      return this.#variantMap;
    }

    try {
      const parsed = JSON.parse(script.textContent);
      this.#variantMap = Array.isArray(parsed?.variants) ? parsed : null;
    } catch {
      this.#variantMap = null;
    }

    return this.#variantMap;
  }

  variantFromMap(button, state = this.selectedCardOptionState(button)) {
    const map = this.quickAddVariantMap();
    if (!map?.variants?.length) return null;
    if (state.productUrl && map.productUrl && !sameProductPath(state.productUrl, map.productUrl)) return null;

    const optionCount = this.optionCount();
    if (optionCount <= 0 || state.selectedValuesByPosition.size < optionCount) return null;

    const selectedOptions = [];
    for (let position = 1; position <= optionCount; position += 1) {
      const optionName = state.selectedNamesByPosition.get(position);
      if (!optionName) return null;
      selectedOptions.push(String(optionName));
    }

    const match = map.variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      return selectedOptions.every((optionName, index) => String(variant.options[index] ?? '') === optionName);
    });

    if (!match) {
      return { id: '', available: false, known: true, nonexistent: true };
    }

    return {
      id: match.id ? String(match.id) : '',
      available: match.available === true,
      known: true,
      nonexistent: false,
    };
  }

  buttonAvailability(button) {
    if (!(button instanceof HTMLElement)) return { available: false, complete: false, known: false, variantId: '' };

    const optionCount = this.optionCount();
    if (optionCount <= 0) return { available: false, complete: false, known: false, variantId: '' };

    const state = this.selectedCardOptionState(button);
    const complete = state.selectedValuesByPosition.size >= optionCount;
    if (!complete) return { available: false, complete: false, known: false, variantId: '' };

    const mappedVariant = this.variantFromMap(button, state);
    if (mappedVariant?.known) {
      return {
        available: mappedVariant.available,
        complete: true,
        known: true,
        variantId: mappedVariant.id,
      };
    }

    const fallbackKey = this.fallbackAvailabilityKey(button);
    if (fallbackKey && this.#fallbackUnavailableKeys.has(fallbackKey)) {
      return { available: false, complete: true, known: true, variantId: '' };
    }

    return { available: true, complete: true, known: false, variantId: '' };
  }

  refreshAvailability(panel = null) {
    const sizePanel = panel ?? this.#host.querySelector('.quick-add__size-selector');
    if (!sizePanel) return;

    sizePanel.querySelectorAll('.quick-add__size-button').forEach((btn) => {
      const optionValue = btn.dataset.optionValue || btn.dataset.sizeValue;
      if (!optionValue) return;
      const state = this.buttonAvailability(btn);
      const available = state.available;
      btn.classList.toggle('quick-add__size-button--unavailable', !available);
      btn.classList.toggle('fit-quick-add__size-button--unavailable', !available);
      btn.setAttribute('aria-disabled', available ? 'false' : 'true');
      if (state.known) {
        btn.dataset.quickAddAvailability = available ? 'available' : 'unavailable';
        btn.dataset.optionAvailable = available ? 'true' : 'false';
      } else {
        delete btn.dataset.quickAddAvailability;
      }
      if (state.variantId) {
        btn.dataset.variantId = state.variantId;
      }
      if (available) {
        btn.removeAttribute('disabled');
      } else {
        btn.setAttribute('disabled', '');
        btn.classList.remove('quick-add__size-button--selected');
      }
    });
  }

  async resolveVariantFromButton(button) {
    if (!(button instanceof HTMLElement)) return null;
    const buttonState = this.buttonAvailability(button);
    if (!buttonState.available) return null;
    if (buttonState.known && buttonState.variantId) {
      return { id: buttonState.variantId, available: true };
    }

    const selectedOptionValueIds = this.selectedOptionValueIds(button);
    const optionCount = this.optionCount();

    if (optionCount === 1 && button.dataset.variantId) {
      return { id: button.dataset.variantId, available: button.dataset.optionAvailable !== 'false' };
    }

    const doc = await this.fetchSelectedProductCard(button, selectedOptionValueIds);
    const variant = this.extractSelectedVariant(doc);
    const fallbackKey = this.fallbackAvailabilityKey(button, selectedOptionValueIds);

    if (!variant?.id || variant.available === false) {
      this.rememberFallbackUnavailable(fallbackKey);
      button.classList.add('quick-add__size-button--unavailable', 'fit-quick-add__size-button--unavailable');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('disabled', '');
      button.classList.remove('quick-add__size-button--selected');
      return null;
    }

    if (fallbackKey) this.#fallbackUnavailableKeys.delete(fallbackKey);

    return { id: variant.id, available: true };
  }

  selectedProductCardRequestUrl(button, selectedOptionValueIds) {
    const { productUrl } = this.selectedCardOptionState(button);
    if (!productUrl) return null;

    const url = preserveMarketContext(new URL(productUrl, window.location.origin));
    url.searchParams.set('section_id', 'section-rendering-product-card');
    if (selectedOptionValueIds.length) {
      url.searchParams.set('option_values', selectedOptionValueIds.join(','));
    }

    return url;
  }

  fallbackAvailabilityKey(button, selectedOptionValueIds = this.selectedOptionValueIds(button)) {
    const url = this.selectedProductCardRequestUrl(button, selectedOptionValueIds);
    if (!url) return '';

    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return url.toString();
  }

  rememberFallbackUnavailable(key) {
    if (!key) return;

    this.#fallbackUnavailableKeys.add(key);
    if (this.#fallbackUnavailableKeys.size <= QUICK_ADD_FALLBACK_AVAILABILITY_LIMIT) return;

    const [firstKey] = this.#fallbackUnavailableKeys;
    this.#fallbackUnavailableKeys.delete(firstKey);
  }

  async fetchSelectedProductCard(button, selectedOptionValueIds) {
    const url = this.selectedProductCardRequestUrl(button, selectedOptionValueIds);
    if (!url) return null;

    return sectionManager.getHTMLDocument(url, {
      cache: window.Shopify?.designMode !== true,
      signal: this.#host.lifecycleSignal,
    });
  }

  prewarmButton(button) {
    if (!(button instanceof HTMLElement) || window.Shopify?.designMode === true) return;
    const state = this.buttonAvailability(button);
    if (!state.complete || !state.available) return;
    if (state.known && state.variantId) return;

    const selectedOptionValueIds = this.selectedOptionValueIds(button);
    if (!selectedOptionValueIds.length) return;

    const url = this.selectedProductCardRequestUrl(button, selectedOptionValueIds);
    if (!url) return;

    const key = url.toString();
    if (this.#prewarmedProductCardUrls.has(key)) return;
    this.#prewarmedProductCardUrls.add(key);

    if (this.#prewarmedProductCardUrls.size > QUICK_ADD_PREWARM_LIMIT) {
      const [firstKey] = this.#prewarmedProductCardUrls;
      this.#prewarmedProductCardUrls.delete(firstKey);
    }

    void sectionManager
      .getHTMLDocument(url, {
        cache: true,
      })
      .then((doc) => {
        if (!doc) {
          this.#prewarmedProductCardUrls.delete(key);
          return;
        }
        preloadCardImage(doc);
      })
      .catch(() => {
        this.#prewarmedProductCardUrls.delete(key);
      });
  }

  extractSelectedVariant(doc) {
    const script = doc?.querySelector?.(
      'product-card variant-select script[type="application/json"], product-card swatch-picker script[type="application/json"], variant-select script[type="application/json"], swatch-picker script[type="application/json"]',
    );
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }
}
